import React, { createContext, useContext, useEffect, useState } from "react";
import { Theme } from "@radix-ui/themes";
import { RadixColor } from "@/types/breeze/Theme";
import { TenantRadius } from "@/types/breeze/Tenant";

type ThemeContextType = {
  isDarkMode: boolean;
  toggleTheme: () => void;
  setPrimaryColor: (color: RadixColor) => void;
  setGrayColor: (
    color: "gray" | "mauve" | "slate" | "sage" | "olive" | "sand",
  ) => void;
  setRadius: (radius: TenantRadius) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = "anythink-theme-preference";

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage first during initialization
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored !== null) {
        return stored !== "light";
      }
    }
    // Fallback to system preference
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: light)").matches;
    }
    return false;
  });

  const [primaryColor, setPrimaryColor] = useState<RadixColor>("orange");
  const [grayColor, setGrayColor] = useState<
    "gray" | "mauve" | "slate" | "sage" | "olive" | "sand"
  >("gray");
  const [radius, setRadius] = useState<TenantRadius>("none");

  useEffect(() => {
    // Initial system preference check
    const mediaQuery = window.matchMedia("(prefers-color-scheme: light)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if there's no stored preference
      if (!localStorage.getItem(THEME_STORAGE_KEY)) {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    // Update document class and localStorage when theme changes
    document.documentElement.classList.toggle("dark", isDarkMode);
    localStorage.setItem(THEME_STORAGE_KEY, isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  return (
    <ThemeContext.Provider
      value={{
        isDarkMode,
        toggleTheme,
        setPrimaryColor,
        setGrayColor,
        setRadius,
      }}
    >
      <Theme
        accentColor={primaryColor}
        grayColor={grayColor}
        appearance={isDarkMode ? "dark" : "light"}
        panelBackground="translucent"
        radius={radius}
        scaling="100%"
      >
        {children}
      </Theme>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
